// Page-level patterns: PageHeader, EmptyState, ErrorState, LoadingSkeleton, StatTile, BrandMark.

import { brand } from '@enp/brand';
import {
  CircleAlert,
  TrendingDown,
  TrendingUp,
  TriangleAlert,
  type LucideIcon,
} from 'lucide-react';
import type { ComponentProps, ReactNode } from 'react';
import { cn } from '../lib/cn.js';
import { Skeleton } from './layout.js';

// ---------------------------------------------------------------- BrandMark

/** URL of a brand asset, relative to the app's base URL. */
export function brandAssetUrl(path: string): string {
  const base = import.meta.env?.BASE_URL ?? '/';
  return `${base.endsWith('/') ? base : `${base}/`}${path}`;
}

export interface BrandMarkProps extends ComponentProps<'span'> {
  /** Show the product name beside the mark. */
  withName?: boolean;
  /** Extra label after the name, e.g. the app name ("Admin"). */
  suffix?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
}

/** The product mark + name. Both come from @enp/brand, never from code. */
export function BrandMark({
  withName = true,
  suffix,
  size = 'md',
  className,
  ...props
}: BrandMarkProps) {
  const box = { sm: 'size-6', md: 'size-8', lg: 'size-14' }[size];
  const text = { sm: 'text-base', md: 'text-lg', lg: 'text-3xl' }[size];
  return (
    <span className={cn('inline-flex items-center gap-2.5', className)} {...props}>
      {/* Inlined (a data URI), so the mark shows even if the connection dropped at load. */}
      <img src={brand.inline.mark} alt="" className={cn(box, 'shrink-0')} />
      {withName && (
        <span className={cn('font-display font-semibold leading-none tracking-tight', text)}>
          {brand.productName}
          {suffix && (
            <span className="ml-1.5 font-body text-[0.7em] font-medium text-muted">{suffix}</span>
          )}
        </span>
      )}
    </span>
  );
}

// ---------------------------------------------------------------- PageHeader

export interface PageHeaderProps extends Omit<ComponentProps<'header'>, 'title'> {
  title: ReactNode;
  description?: ReactNode;
  /** Primary (and secondary) actions, rendered on the right. */
  actions?: ReactNode;
  /** Breadcrumb or back link above the title. */
  eyebrow?: ReactNode;
  /** Heading level of the title (h1 on a real page; lower when shown as an example). */
  titleAs?: 'h1' | 'h2' | 'h3' | 'h4';
}

export function PageHeader({
  title,
  description,
  actions,
  eyebrow,
  titleAs: Heading = 'h1',
  className,
  ...props
}: PageHeaderProps) {
  return (
    <header
      className={cn('flex flex-wrap items-end justify-between gap-x-6 gap-y-3 pb-6', className)}
      {...props}
    >
      <div className="flex min-w-0 flex-col gap-1.5">
        {eyebrow}
        <Heading className="font-display text-2xl font-semibold leading-tight tracking-tight text-fg comfortable:text-3xl">
          {title}
        </Heading>
        {description && <p className="max-w-prose text-body text-muted">{description}</p>}
      </div>
      {/* When they wrap under the title they stay at the right, primary last (UI8C-12). */}
      {actions && (
        <div className="ml-auto flex shrink-0 flex-wrap items-center justify-end gap-2">
          {actions}
        </div>
      )}
    </header>
  );
}

// ---------------------------------------------------------------- EmptyState

export interface EmptyStateProps extends Omit<ComponentProps<'div'>, 'title'> {
  icon: LucideIcon;
  title: ReactNode;
  /** What this place is for, and what to do next. */
  description?: ReactNode;
  /** The next step (usually one primary Button). */
  action?: ReactNode;
  compact?: boolean;
  /** Heading level of the title: h1 when the empty state IS the page. */
  titleAs?: 'h1' | 'h2' | 'h3';
  /** `success`: nothing is left to do (the icon in the success tone, not the brand's). */
  tone?: 'default' | 'success';
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  compact = false,
  titleAs: Heading = 'h2',
  tone = 'default',
  className,
  ...props
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-border-strong/70 bg-surface text-center',
        compact ? 'px-6 py-8' : 'px-8 py-14',
        className,
      )}
      {...props}
    >
      <span
        className={cn(
          'flex size-12 items-center justify-center rounded-full comfortable:size-16',
          tone === 'success'
            ? 'bg-state-success-subtle text-state-success'
            : 'bg-primary-subtle text-primary-text',
        )}
      >
        <Icon aria-hidden className="size-6 comfortable:size-8" />
      </span>
      <div className="flex max-w-md flex-col gap-1">
        <Heading className="font-display text-lg font-semibold text-fg comfortable:text-2xl">
          {title}
        </Heading>
        {description && <p className="text-body text-muted">{description}</p>}
      </div>
      {action && <div className="mt-2 flex flex-wrap justify-center gap-2">{action}</div>}
    </div>
  );
}

// ---------------------------------------------------------------- ErrorState

export interface ErrorStateProps extends Omit<ComponentProps<'div'>, 'title'> {
  title?: ReactNode;
  /** Plain words: what went wrong and whether anything was lost. */
  message: ReactNode;
  /** A way out: retry, go back, contact support. At least one. */
  actions: ReactNode;
  /** Technical detail (error id), shown small for support. */
  detail?: ReactNode;
  /**
   * `danger` (default): something failed. `warning`: a condition the user can wait out or
   * fix, such as being offline. Not every problem is red.
   */
  tone?: 'danger' | 'warning';
  /** Icon beside the title (defaults to an alert icon for the tone). */
  icon?: LucideIcon;
  /** Heading level of the title: h1 when the error IS the page. */
  titleAs?: 'h1' | 'h2' | 'h3';
}

const ERROR_TONES = {
  danger: {
    box: 'border-state-danger/35 bg-state-danger-subtle',
    text: 'text-state-danger',
    icon: CircleAlert,
  },
  warning: {
    box: 'border-state-warning/40 bg-state-warning-subtle',
    text: 'text-state-warning',
    icon: TriangleAlert,
  },
} as const;

export function ErrorState({
  title = 'Something went wrong',
  message,
  actions,
  detail,
  tone = 'danger',
  icon,
  titleAs: Heading = 'h2',
  className,
  ...props
}: ErrorStateProps) {
  const t = ERROR_TONES[tone];
  const Icon = icon ?? t.icon;
  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      data-tone={tone}
      className={cn(
        'flex flex-col items-start gap-3 rounded-lg border px-5 py-4 text-fg',
        t.box,
        className,
      )}
      {...props}
    >
      <div className="flex items-start gap-3">
        <Icon aria-hidden className={cn('mt-0.5 size-5 shrink-0 comfortable:size-6', t.text)} />
        <div className="flex flex-col gap-1">
          {/* The heading is never smaller than the message under it. */}
          <Heading className={cn('text-base font-semibold comfortable:text-xl', t.text)}>
            {title}
          </Heading>
          <p className="text-body text-fg">{message}</p>
          {detail && (
            <p className="font-mono text-xs text-muted comfortable:text-label">{detail}</p>
          )}
        </div>
      </div>
      <div className="flex flex-wrap gap-2 pl-8 comfortable:pl-9">{actions}</div>
    </div>
  );
}

// ---------------------------------------------------------------- LoadingSkeleton

export interface LoadingSkeletonProps extends ComponentProps<'div'> {
  variant: 'table' | 'cards' | 'form' | 'page' | 'text';
  /** Rows (table) or items (cards/text). */
  count?: number;
  /** Screen-reader text. */
  label?: string;
}

export function LoadingSkeleton({
  variant,
  count,
  label = 'Loading',
  className,
  ...props
}: LoadingSkeletonProps) {
  return (
    <div role="status" aria-live="polite" className={cn('w-full', className)} {...props}>
      <span className="sr-only">{label}…</span>
      {variant === 'table' && (
        <div className="overflow-hidden rounded-lg border border-border bg-surface-raised">
          <div className="flex gap-4 border-b border-border bg-surface-sunken px-3 py-3">
            {['w-40', 'w-24', 'w-20', 'w-16'].map((w) => (
              <Skeleton key={w} className={cn('h-3', w)} />
            ))}
          </div>
          {Array.from({ length: count ?? 6 }, (_, i) => (
            <div
              key={i}
              className="grid grid-cols-[2fr_1fr_1fr_1fr] gap-4 border-b border-border px-3 py-3 last:border-0"
            >
              <Skeleton className="h-4" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-4 w-2/3" />
            </div>
          ))}
        </div>
      )}
      {variant === 'cards' && (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(14rem,1fr))] gap-4">
          {Array.from({ length: count ?? 4 }, (_, i) => (
            <div
              key={i}
              className="flex flex-col gap-3 rounded-lg border border-border bg-surface-raised p-5"
            >
              <Skeleton className="h-3 w-1/2" />
              <Skeleton className="h-7 w-2/3" />
              <Skeleton className="h-3 w-1/3" />
            </div>
          ))}
        </div>
      )}
      {variant === 'form' && (
        <div className="flex max-w-lg flex-col gap-5">
          {Array.from({ length: count ?? 3 }, (_, i) => (
            <div key={i} className="flex flex-col gap-2">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-control w-full" />
            </div>
          ))}
          <Skeleton className="h-control w-32" />
        </div>
      )}
      {variant === 'page' && (
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-2">
            <Skeleton className="h-7 w-64" />
            <Skeleton className="h-4 w-96 max-w-full" />
          </div>
          <Skeleton className="h-64 w-full rounded-lg" />
        </div>
      )}
      {variant === 'text' && (
        <div className="flex flex-col gap-2">
          {Array.from({ length: count ?? 3 }, (_, i) => (
            <Skeleton key={i} className={cn('h-4', i % 3 === 2 ? 'w-2/3' : 'w-full')} />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- StatTile

export interface StatTileProps extends ComponentProps<'div'> {
  label: ReactNode;
  /** The figure. `null` shows a dash, announced as "Not available yet". */
  value: ReactNode | null;
  /** Context under the figure ("12 lots", "since 06:00"). */
  hint?: ReactNode;
  /** Trend text with direction; direction is shown by icon + words, not colour alone. */
  trend?: { direction: 'up' | 'down'; text: ReactNode; good?: boolean };
  icon?: LucideIcon;
  tone?: 'neutral' | 'danger' | 'warning' | 'success';
  loading?: boolean;
}

export function StatTile({
  label,
  value,
  hint,
  trend,
  icon: Icon,
  tone = 'neutral',
  loading = false,
  className,
  ...props
}: StatTileProps) {
  // Only a tone that means something gets a coloured edge; neutral tiles have none.
  const toneBar = {
    neutral: undefined,
    danger: 'before:bg-state-danger',
    warning: 'before:bg-state-warning',
    success: 'before:bg-state-success',
  }[tone];
  const TrendIcon = trend?.direction === 'down' ? TrendingDown : TrendingUp;
  return (
    <div
      className={cn(
        'relative flex flex-col gap-1.5 overflow-hidden rounded-lg border border-border bg-surface-raised p-5 shadow-xs',
        toneBar && "before:absolute before:inset-y-0 before:left-0 before:w-1 before:content-['']",
        toneBar,
        className,
      )}
      {...props}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-muted comfortable:text-label">{label}</span>
        {Icon && <Icon aria-hidden className="size-4 text-muted comfortable:size-5" />}
      </div>
      {loading ? (
        <Skeleton className="h-8 w-24" />
      ) : (
        <span className="font-display text-figure font-semibold tabular-nums tracking-tight text-fg">
          {value ?? (
            <>
              <span aria-hidden>—</span>
              <span className="sr-only">Not available yet</span>
            </>
          )}
        </span>
      )}
      {(hint || trend) && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted comfortable:text-label">
          {trend && (
            <span
              className={cn(
                'inline-flex items-center gap-1 font-medium',
                trend.good === undefined
                  ? 'text-fg'
                  : trend.good
                    ? 'text-state-success'
                    : 'text-state-danger',
              )}
            >
              <TrendIcon aria-hidden className="size-4" />
              {trend.text}
            </span>
          )}
          {hint && <span>{hint}</span>}
        </div>
      )}
    </div>
  );
}
